import { makeStyles } from '@material-ui/core/styles';
import React, { useState, useEffect } from 'react';
import { Paper, Grid } from '@material-ui/core';
import CustomTable from 'components/CustomTable';
import NumberListFormItem from 'components/Application/InitialApplication/NumberListFormItem';
import CustomButton from 'components/CustomInput/Button';
import PopupInput from 'components/Application/TeamMemberForm/PopupInput';
import useDocumentUploadData from 'hooks/tableData/useDocumentUploadData';
import API from 'api/call';
import { setGlobalLoading } from 'store/global/reducer';
import {
    UPDATE_APPLICATION_DETAIL_POST_DATA,
    ADD_DELETE_REQUEST,
    REMOVE_DELETE_REQUEST,
    UPDATE_FORM_VALIDITY,
    SET_STUDY_VALIDATION,
    SET_SITE_VALIDATION,
    SET_DOCUMENT_UPLOAD,
} from 'store/key';
import { useSelector, useDispatch } from 'react-redux';
import { isArray } from 'lodash';
import { getTimeWithTimeZone, produce, toHalfWidthUnicode } from 'utility/general';
import TextHeader from 'components/CustomText/TextHeader';
import { checkSiteInfoStudent } from 'utility/formValidate';

const useStyles = makeStyles((theme) => ({
    paperMargin: {
        margin: 10,
    },
    gridPadding: {
        padding: 10,
    },
    formTitleWeight: {
        fontWeight: 'bold',
    },
    num: {
        marginRight: theme.spacing(1),
    },
    subText: {
        paddingLeft: theme.spacing(5),
        paddingTop: theme.spacing(1),
    },
    item: {
        paddingBottom: theme.spacing(2),
    },
    tableBox: {
        paddingTop: theme.spacing(1),
    },
}));

const DocumentUploadFormLayout = (props) => {
    const classes = useStyles();
    const {
        tabType,
        siteInfoId,
        formId,
        disableOnPermission,
        isAmendmentEdit,
        isAmendmentCreate,
        applicationCreateDtm,
        allComments,
        getCommentDetail,
        isEnableComment,
    } = props;

    const onlyShowInStudyInfo = tabType === 'info';
    const isStudyInfo = onlyShowInStudyInfo;
    const isAmendmentForm = isAmendmentEdit || isAmendmentCreate;

    const [openPopup, setOpenPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedContentItem, setSelectedContentItem] = useState(null);

    const dispatch = useDispatch();
    const userInfo = useSelector((state) => state.user.detail);
    const applicationDetailPostData = useSelector((state) => state.applicationDetailPostData);
    const applicationDetail = useSelector((state) => state.applicationDetail[formId]);
    const handledSuDocDeleteRequest = useSelector((state) => state.documentUpload.handledSuDocDeleteRequest);

    const postDeleteRequest = applicationDetailPostData.deleteRequest ?? {};

    const userName = `${userInfo.firstName} ${userInfo.surname}`;

    const postData = applicationDetailPostData.postData;
    const postCTI = postData.cti ?? [];
    const postUploadDocuments = postData.uploadDocuments ?? [];
    const postDataSiteInfo = postData.siteInfo ?? [];
    const uploadDocumentsDeleteRequest = postDeleteRequest.uploadDocuments ?? [];

    const {
        contentList,
        initRows,
        researchProtocolRows,
        declarationRows,
        indemnityAgreementRows,
        insuranceCertificateRows,
        supportingDocumentRows,
        decisionDocumentRows,
        signedApplicationFormRows,
    } = useDocumentUploadData({
        ...props,
        tabType,
        formId,
        siteInfoId,
        isStudyInfo,
    });

    const [formType, setFormType] = useState(null);

    /** Indemnity Agreement (Mandatory for sponsored trial)	Part IV 18.3 = Yes (type: 6)*/
    const postP4Sponsor = applicationDetailPostData.postData.applicationForm?.p4Sponsor;
    const [p4Sponsor, setP4Sponsor] = useState(applicationDetail?.applicationForm?.p4Sponsor);

    /** Clinical Trial Insurance Certificate	Part IV 21.4 = Yes  (type: 'cti')*/
    const postP4Insurance = applicationDetailPostData.postData.applicationForm?.p4Insurance;
    const [p4Insurance, setP4Insurance] = useState(applicationDetail?.applicationForm?.p4Insurance);

    /** Supporting document from Academia (Mandatory for student project)	Personal Particular 2 = Student(site info) (type: 7) */
    const [student, setStudent] = useState(null);

    /** CIRB/CREC/IRB decision document of clinical research ethics review for cross-cluster study (Mandatory if applicable)	Part I 3.1 = Yes  (type: 8)*/
    const postP1ReviewByRecIrb = applicationDetailPostData.postData.applicationForm?.p1ReviewByRecIrb;
    const [p1ReviewByRecIrb, setP1ReviewByRecIrb] = useState(applicationDetail?.applicationForm?.p1ReviewByRecIrb);
    const postP1RecIrbDecision = applicationDetailPostData.postData.applicationForm?.p1RecIrbDecision;
    const [p1RecIrbDecision, setP1RecIrbDecision] = useState(applicationDetail?.applicationForm?.p1RecIrbDecision);
    const siteInfoList = applicationDetail?.siteInfo;

    const [studyInfoValidation, setStudyInfoValidation] = useState({});
    const [siteInfoValidation, setSiteInfoValidation] = useState({});
    const validationKey = 'documentUploadValid';
    // when studyInfoValidation change, update the reducer
    useEffect(() => {
        dispatch({
            type: SET_STUDY_VALIDATION,
            payload: {
                [validationKey]: studyInfoValidation,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studyInfoValidation]);

    // when siteInfoValidation change, update the reducer
    useEffect(() => {
        dispatch({
            type: SET_SITE_VALIDATION,
            payload: {
                siteInfoId,
                [validationKey]: siteInfoValidation,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteInfoValidation]);

    const updateStudyValidation = (name, isValidate) => {
        setStudyInfoValidation(
            produce((draft) => {
                draft[name] = isValidate;
            }),
        );
    };

    const updateSiteValidation = (name, isValidate) => {
        setSiteInfoValidation(
            produce((draft) => {
                draft[name] = isValidate;
            }),
        );
    };

    const checkSiteIndemnityAgreementRows = (p4Sponsor) => {
        for (let i of applicationDetail.siteInfo) {
            let valid = true;
            let declarationRows = [],
                signedApplicationFormRows = [],
                indemnityAgreementRows = [];
            for (let n of applicationDetail.uploadDocuments) {
                if (i.id === n.siteInfo?.id) {
                    switch (n.type) {
                        case 5:
                            declarationRows.push(n);
                            break;
                        case 6:
                            indemnityAgreementRows.push(n);
                            break;
                        case 10:
                            signedApplicationFormRows.push(n);
                            break;

                        default:
                            break;
                    }
                }
            }
            valid = declarationRows.length !== 0;
            valid = signedApplicationFormRows.length !== 0;
            // if (p4Sponsor) {
            //     valid = indemnityAgreementRows.length !== 0;
            // }

            dispatch({
                type: UPDATE_FORM_VALIDITY,
                payload: {
                    type: 'site',
                    applicationFormId: formId,
                    siteInfoId: i.id,
                    formKey: 'documentUploadValid',
                    isValid: valid,
                },
            });
        }
    };

    /** Check mandatory form for completeness */
    useEffect(() => {
        let completed = true;
        switch (tabType) {
            case 'info':
                /** study info */
                if (!mapRows(researchProtocolRows).length) {
                    completed = false;
                    updateStudyValidation('docUpload_0', false);
                } else {
                    updateStudyValidation('docUpload_0', true);
                }

                // Q.29 is optional
                // if (p4Insurance && !mapRows(insuranceCertificateRows).length) {
                //     completed = false;
                //     updateStudyValidation('docUpload_cti', false);
                // } else {
                //     updateStudyValidation('docUpload_cti', true);
                // }

                if (student && !mapRows(supportingDocumentRows).length && !isAmendmentForm) {
                    completed = false;
                    updateStudyValidation('docUpload_7', false);
                } else {
                    updateStudyValidation('docUpload_7', true);
                }

                if (
                    p1ReviewByRecIrb &&
                    !mapRows(decisionDocumentRows).length &&
                    (p1RecIrbDecision === 'A' || p1RecIrbDecision === 'D')
                ) {
                    completed = false;
                    updateStudyValidation('docUpload_8', false);
                } else {
                    updateStudyValidation('docUpload_8', true);
                }

                /** check Indemnity Agreement has records in table */
                checkSiteIndemnityAgreementRows(p4Sponsor);

                dispatch({
                    type: UPDATE_FORM_VALIDITY,
                    payload: {
                        type: 'study',
                        applicationFormId: formId,
                        siteInfoId: siteInfoId,
                        formKey: 'documentUploadValid',
                        isValid: completed,
                    },
                });
                break;

            default:
                /** tabType = hospital (site info) */
                if (!mapRows(declarationRows).length) {
                    completed = false;
                    updateSiteValidation('docUpload_5', false);
                } else {
                    updateSiteValidation('docUpload_5', true);
                }

                if (!mapRows(signedApplicationFormRows).length) {
                    completed = false;
                    updateSiteValidation('docUpload_10', false);
                } else {
                    updateSiteValidation('docUpload_10', true);
                }

                // if (p4Sponsor && !mapRows(indemnityAgreementRows).length && !isAmendmentForm) {
                //     completed = false;
                //     updateSiteValidation('docUpload_6', false);
                // } else {
                //     updateSiteValidation('docUpload_6', true);
                // }

                dispatch({
                    type: UPDATE_FORM_VALIDITY,
                    payload: {
                        type: 'site',
                        applicationFormId: formId,
                        siteInfoId: siteInfoId,
                        formKey: 'documentUploadValid',
                        isValid: completed,
                    },
                });
                break;
        }
        // eslint-disable-next-line
    }, [
        tabType,
        p4Sponsor,
        p4Insurance,
        student,
        p1ReviewByRecIrb,
        p1RecIrbDecision,
        researchProtocolRows,
        insuranceCertificateRows,
        supportingDocumentRows,
        decisionDocumentRows,
        declarationRows,
        signedApplicationFormRows,
        indemnityAgreementRows,
    ]);
    /** end */

    // trigger validation
    useEffect(() => {
        if (postP1RecIrbDecision !== undefined) setP1RecIrbDecision(postP1RecIrbDecision);
    }, [postP1RecIrbDecision]);

    useEffect(() => {
        if (isArray(siteInfoList)) {
            let res = checkSiteInfoStudent(siteInfoList, postDataSiteInfo);
            setStudent(res);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteInfoList]);

    useEffect(() => {
        /** Personal Particular 2 = Student(site info)*/
        if (student !== null && student !== undefined) {
            if (disableOnPermission && isAmendmentForm) return;
            if (!student) {
                dimTable(7);
            } else {
                showTable(7);
            }
        }
        // eslint-disable-next-line
    }, [student]);

    const changeToSiteData = (row) => {
        if (!onlyShowInStudyInfo) {
            return {
                ...row,
                siteInfo: { id: row.siteInfo?.id ?? siteInfoId },
            };
        }
        return row;
    };

    /** Add dim logic start */
    useEffect(() => {
        /** Part IV 18.3 = Yes (type: 6)*/
        if (postP4Sponsor !== undefined) {
            if (disableOnPermission && isAmendmentForm) return;
            setP4Sponsor(postP4Sponsor);
            if (!postP4Sponsor) {
                dimTable(6);
            } else {
                showTable(6);
            }
        }

        /** Part IV 21.4 = Yes  (type: 'cti')*/
        // if (postP4Insurance !== undefined) {
        //     setP4Insurance(postP4Insurance);
        //     if (!postP4Insurance) {
        //         dimTable('cti');
        //     } else {
        //         showTable('cti');
        //     }
        // }

        /** Part I 3.1 = Yes  (type: 8)*/
        if (postP1ReviewByRecIrb !== undefined) {
            setP1ReviewByRecIrb(postP1ReviewByRecIrb);
            if (!postP1ReviewByRecIrb) {
                dimTable(8);
            } else {
                showTable(8);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postP4Sponsor, postP4Insurance, postP1ReviewByRecIrb]);

    const showTable = (type) => {
        const defaultRows = initRows.current[type].defaultRows;
        /** reset table data */
        initRows.current[type].setRows(defaultRows);

        const key = type === 'cti' ? 'cti' : 'uploadDocuments';
        /** reset Delete Row To RequestData */
        if (defaultRows.length) {
            const copyPostDeleteRequest = JSON.parse(JSON.stringify(postDeleteRequest));
            for (let i of defaultRows) {
                if (!copyPostDeleteRequest[key]) return;
                const index = copyPostDeleteRequest[key].findIndex((n) => n.id === i.id);
                if (index !== -1) {
                    if (handledSuDocDeleteRequest.includes(i.id)) return;
                    dispatch({
                        type: REMOVE_DELETE_REQUEST,
                        payload: {
                            type: key,
                            id: i.id,
                            applicationFormId: formId,
                        },
                    });
                }
            }
        }
    };

    const dimTable = (type) => {
        const defaultRows = initRows.current[type].defaultRows;
        /** clear table data */
        initRows.current[type].setRows([]);

        const key = type === 'cti' ? 'cti' : 'uploadDocuments';
        const list = type === 'cti' ? postCTI : postUploadDocuments;
        /** delete default rows from get get-detail-application api */
        if (defaultRows.length) {
            addDeleteRowToRequestData(defaultRows, key);
        }

        /** reset reducer's postData */
        if (!list.length) return;
        let tempArr = [];
        if (key !== 'cti') {
            for (let i of list) {
                if (i.type !== type) {
                    tempArr.push(changeToSiteData(i));
                }
            }
        }
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: key,
                data: tempArr,
                applicationFormId: formId,
            },
        });
    };
    const addDeleteRowToRequestData = (rows, key) => {
        for (let i of rows) {
            const index = uploadDocumentsDeleteRequest.findIndex((item) => item.id === i.id);
            if (index !== -1) return;
            let data = {
                type: key,
                id: i.id,
                siteInfoId: i.siteInfo?.id ?? siteInfoId,
                applicationFormId: formId,
            };
            if (!onlyShowInStudyInfo) {
                data.siteInfoId = i.siteInfo?.id ?? siteInfoId;
            }
            dispatch({
                type: ADD_DELETE_REQUEST,
                payload: data,
            });
        }
    };
    /** Add dim logic end */

    const updateDeleteRequestData = (row, formType) => {
        if (formType === 7) {
            dispatch({
                type: SET_DOCUMENT_UPLOAD,
                payload: {
                    handledSuDocDeleteRequest: [...handledSuDocDeleteRequest, row.id],
                },
            });
        }
        /** set data into deleteRequest for postData*/
        const key = row.type === 'cti' ? 'cti' : 'uploadDocuments';
        let data = {
            type: key,
            id: row.id,
            applicationFormId: formId,
        };
        if (!onlyShowInStudyInfo) {
            data.siteInfoId = row.siteInfo?.id ?? siteInfoId;
        }
        dispatch({
            type: ADD_DELETE_REQUEST,
            payload: data,
        });
    };

    const updateEditRequestData = (row) => {
        /** set data into deleteRequest for postData*/
        const index = postCTI.findIndex((i) => i.id === row.id);
        let data = [...postCTI];
        const tempObj = {
            id: row.id,
            expiryDtm: new Date(row.expiryDtm),
            policyNo: row.policyNo,
            uploadTempId: row.uploadTempId,
        };
        if (index === -1) {
            data.push(changeToSiteData(tempObj));
        } else {
            data[index] = changeToSiteData(tempObj);
        }
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: 'cti',
                data: data,
                applicationFormId: formId,
            },
        });
    };

    const deleteRow = (selectedContentItem, row, formType) => {
        const rowsCopy = [...selectedContentItem.rows];
        const index = rowsCopy.findIndex((item, index) => {
            return item.id === row.id;
        });

        let postData = formType === 'cti' ? postCTI : postUploadDocuments;
        const postIndex = postData.findIndex((i) => i.id === row.id);
        if (postIndex !== -1) {
            postData.splice(postIndex, 1);
            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: formType,
                    data: postData,
                    applicationFormId: formId,
                },
            });
        }

        if (index !== -1) {
            if (row.existInDB) {
                updateDeleteRequestData(row, formType);
            }
            rowsCopy.splice(index, 1);
            selectedContentItem.setRows(rowsCopy);
        }
    };
    const editRow = (row) => {
        const rowsCopy = [...selectedContentItem.rows];
        const index = rowsCopy.findIndex((item, index) => item.id === row.id);
        if (index !== -1) {
            if (isArray(row.fileName) && row.fileName.length) {
                uploadFile(row, 'EDIT', (list) => {
                    const newRow = { ...row, fileName: list[0].fileName, uploadTempId: list[0].key };
                    updateEditRequestData(newRow);
                    rowsCopy[index] = {
                        ...row,
                        updateDate: getTimeWithTimeZone(new Date()).format('YYYY-MM-DD'),
                        fileName: list[0].fileName,
                        existInDB: false,
                    };
                    selectedContentItem.setRows(rowsCopy);
                });
            } else {
                updateEditRequestData(row);
                rowsCopy[index] = { ...row, updateDate: getTimeWithTimeZone(new Date()).format('YYYY-MM-DD') };
                selectedContentItem.setRows(rowsCopy);
            }
        }
    };

    const updateUploadData = (list, data) => {
        if (list.length) {
            let tempRows = [],
                tempCti = [...postCTI],
                tempDoc = [...postUploadDocuments];
            for (let i = 0; i < list.length; i++) {
                const tempId = data.id + i;
                let row = {
                    createdByFullName: userName,
                    updateDate: getTimeWithTimeZone(new Date()).format('YYYY-MM-DD'),
                    fileName: list[i].fileName,
                    id: tempId,
                    uploadTempId: list[i].key,
                };

                if (formType === 'cti') {
                    row = {
                        ...row,
                        expiryDtm: data.expiryDtm,
                        policyNo: data.policyNo,
                    };
                    tempCti.push(
                        changeToSiteData({
                            id: tempId,
                            expiryDtm: new Date(data.expiryDtm),
                            policyNo: data.policyNo,
                            uploadTempId: list[i].key,
                        }),
                    );
                } else {
                    tempDoc.push(
                        changeToSiteData({
                            id: tempId,
                            type: formType,
                            uploadTempId: list[i].key,
                        }),
                    );
                }
                tempRows.push(row);
            }
            /** update postData*/
            if (tempCti.length !== postCTI.length) {
                dispatch({
                    type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                    payload: {
                        type: 'cti',
                        data: tempCti,
                        applicationFormId: formId,
                    },
                });
            }
            if (tempDoc.length !== postUploadDocuments.length) {
                dispatch({
                    type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                    payload: {
                        type: 'uploadDocuments',
                        data: tempDoc,
                        applicationFormId: formId,
                    },
                });
            }
            /** set rows to frontend display*/
            selectedContentItem.setRows([...selectedContentItem.rows, ...tempRows]);
        }
    };

    const uploadFile = (data, action = 'ADD', callback) => {
        if (formType !== null && data.fileName.length) {
            let formData = new FormData();
            for (let i = 0; i < data.fileName.length; i++) {
                let file = data.fileName[i];

                try {
                    // convert the file name to all half width unicode for fix of code scan issue
                    file = new File([data.fileName[i]], toHalfWidthUnicode(data.fileName[i].name), {
                        type: data.fileName[i].type,
                    });
                } catch (error) {
                    console.log('%c 121236 DocumentUploadFormLayout.jsx error', 'background: green', error);
                }

                formData.append('file', file);
            }
            setGlobalLoading(true);

            API.file
                .uploadDocument(formData)
                .then((res) => {
                    const list = res.data?.result?.result;
                    action === 'EDIT' ? callback?.(list) : updateUploadData(list, data);
                })
                .finally(() => {
                    setGlobalLoading(false);
                });
        }
    };

    /** Add records */
    const onSubmit = (data) => {
        uploadFile(data);
    };

    /** map each site/study rows data */
    const mapRows = (rows) => {
        let newRows = [];
        rows?.forEach((item) => {
            if (isStudyInfo && item.siteInfo === null) {
                newRows.push(item);
            } else if (!isStudyInfo && item.siteInfo?.id === siteInfoId) {
                newRows.push(item);
            }
        });

        const citArr = postDeleteRequest?.cti ?? [];
        const docArr = postDeleteRequest?.uploadDocuments ?? [];
        const ctiDeleteIds = citArr.map((item) => item.id);
        const docDeleteIds = docArr.map((item) => item.id);

        newRows = newRows.filter((item) => ![...ctiDeleteIds, ...docDeleteIds].includes(item.id));
        return newRows;
    };

    const isHighlight = (type) => {
        let ishl = false;
        let objID = formId;
        if (tabType === 'hospital') {
            objID = siteInfoId;
        }
        if (type === 'cti') {
            const changedItem = applicationDetail?.changedItem?.changedItem;
            if (changedItem && changedItem[`${type}`] && changedItem[`${type}`][`${objID}`]) {
                ishl = true;
            }
        } else if (tabType !== 'hospital') {
            const changedItem = applicationDetail?.changedItem?.changedItem;
            if (changedItem && changedItem.uploadDocuments && changedItem.uploadDocuments[`${type}`]) {
                ishl = true;
            }
        } else {
            const changedItem = applicationDetail?.changedItem?.uploadDocument;
            if (
                changedItem &&
                changedItem.uploadDocuments &&
                changedItem.uploadDocuments[`${objID}`] &&
                changedItem.uploadDocuments[`${objID}`].indexOf(type) > -1
            ) {
                ishl = true;
            }
        }
        return ishl;
    };

    const contentMapping = () => {
        const handleClick = (i, type, row) => {
            setFormType(i.type);
            setSelectedContentItem(i);
            switch (type) {
                case 'add':
                    setSelectedRow(null);
                    setOpenPopup(true);
                    break;
                case 'edit':
                    setSelectedRow(row);
                    setOpenPopup(true);
                    break;
                case 'delete':
                    setSelectedRow(row);
                    deleteRow(i, row, i.type);
                    break;
                default:
                    break;
            }
        };
        return contentList.map((i, index) => {
            let showTable = true;
            switch (i.type) {
                case 6:
                    showTable = p4Sponsor;
                    break;
                // case 'cti':
                //     showTable = p4Insurance;
                // break;
                case 7:
                    showTable = student;
                    break;
                case 8:
                    showTable = p1ReviewByRecIrb;
                    break;
                default:
                    break;
            }
            const rows = mapRows(i.rows);

            return i.show ? (
                <Grid key={i.number} container className={classes.item}>
                    <NumberListFormItem number={i.number} alignItems="baseline" highlight={isHighlight(i.type)}
                        isEnableComment={isEnableComment}
                        commentProps={{ 
                            commentLabel: `${i.number}. ${i.popupTitle}`, 
                            commentStatus: null,
                            commentField: i.popupTitle,
                            commentDetail: getCommentDetail(i.popupTitle, allComments),
                        }}
                    >
                        <Grid
                            style={{ color: i.labelColor }}
                            container
                            className={classes.formTitleWeight}
                            wrap="nowrap"
                            alignItems="baseline"
                        >
                            {i.label}
                        </Grid>
                    </NumberListFormItem>
                    {showTable && i.displayTable && (
                        <Grid container className={classes.tableBox}>
                            <CustomButton
                                disabled={disableOnPermission || (isAmendmentForm && i.type === 'cti') || (i.number === '28')}
                                style={{ marginBottom: 8 }}
                                onClick={() => handleClick(i, 'add')}
                            >
                                Add
                            </CustomButton>
                            <CustomTable
                                disableOnPermission={disableOnPermission}
                                isEditAllow={i.type === 'cti'}
                                onEdit={(row) => handleClick(i, 'edit', row)}
                                isDeleteAllow={i.number !== '28'}
                                onDelete={(row) => handleClick(i, 'delete', row)}
                                columns={i.columns}
                                rows={rows}
                                allowResize
                                fullWidthTable
                                disableOldDataDelete={isAmendmentForm}
                                disableOldDataEdit={isAmendmentForm}
                                applicationCreateDtm={applicationCreateDtm}
                                isRowNumberEnabled
                                indexAddDot={i.indexAddDot}
                                indexHeaderName={i.indexHeaderName}
                            />
                        </Grid>
                    )}
                    {i.toolTips && siteInfoId !== -1 && (
                        <div style={{ marginTop: 20 }}>
                            <i>
                                Form can be downloaded from “Useful Information/User Manual” via the navigation bar on
                                the left.
                            </i>
                        </div>
                    )}
                </Grid>
            ) : null;
        });
    };

    return (
        <div>
            {openPopup && (
                <PopupInput
                    height={selectedContentItem.popupHeight ? selectedContentItem.popupHeight : '50%'}
                    width={selectedContentItem.popupWidth ? selectedContentItem.popupWidth : '50%'}
                    setOpenPopup={setOpenPopup}
                    tableList={selectedContentItem.rows}
                    title={selectedContentItem.popupTitle}
                    defaultFormList={selectedContentItem.defaultFormList}
                    selectedRow={selectedRow}
                    editRow={editRow}
                    onSubmit={onSubmit}
                    returnOriginalData={true}
                />
            )}

            <Paper className={classes.paperMargin}>
                <div className={classes.gridPadding}>
                    <TextHeader italic underline>
                        Document Upload
                    </TextHeader>
                </div>
                <Grid container className={classes.gridPadding}>
                    {contentMapping()}
                </Grid>
            </Paper>
        </div>
    );
};

export default DocumentUploadFormLayout;
